/**
 * "No Karexia access on this account" screen.
 *
 * Shown by ProtectedRoute when the visitor IS signed in on the shared sign-in
 * (realm `core`, also used by unrelated applications) but the account holds no
 * Karexia client role — or the server refused the session with
 * `403 no_karexia_access`.
 *
 * What the account can do next (GET /api/v1/users/me/karexia-access):
 *  1. PENDING INVITATIONS first — each "Accept invitation" opens the ungated
 *     /accept-org-invite/:token route (only listed for a verified email; an
 *     unverified email is told to open the link from the email instead);
 *  2. "Create my workspace" — a new organisation it owns (terms + privacy
 *     acceptance required, optional CASL/Law-25 express marketing consent whose
 *     exact translated wording is sent as proof). With a pending invitation the
 *     creation is secondary and needs an explicit confirmation.
 * On success the token is refreshed (silent renew, else a redirect sign-in) so
 * the OWNER role and the tenant claim appear, then the app reloads on "/" where
 * the normal first-run onboarding takes over.
 *
 * Sign out (back to this origin, so it works on www.karexia.com and
 * admin.karexia.com alike) and "use another account" are always available.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from 'react-oidc-context';

import { resetNoKarexiaAccess } from '@/auth/karexiaAccess';
import { useT } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import {
  ORG_NAME_MAX,
  createWorkspace,
  getKarexiaAccessStatus,
  isValidOrgName,
  normaliseOrgName,
  workspaceErrorCode,
  type KarexiaAccessStatus,
  type WorkspaceErrorCode,
} from '@/lib/api/selfServe';

const ERROR_KEYS: Record<WorkspaceErrorCode, TranslationKey> = {
  pending_invitation: 'shell.auth.noKarexiaAccess.errorPendingInvitation',
  already_has_workspace: 'shell.auth.noKarexiaAccess.errorAlreadyHasWorkspace',
  invalid_org_name: 'shell.auth.noKarexiaAccess.errorInvalidOrgName',
  terms_not_accepted: 'shell.auth.noKarexiaAccess.errorTermsNotAccepted',
  marketing_wording_required: 'shell.auth.noKarexiaAccess.errorMarketingWordingRequired',
  rate_limited: 'shell.auth.noKarexiaAccess.errorRateLimited',
  self_serve_unavailable: 'shell.auth.noKarexiaAccess.errorSelfServeUnavailable',
  self_serve_disabled: 'shell.auth.noKarexiaAccess.errorSelfServeDisabled',
  tenant_provisioning_failed: 'shell.auth.noKarexiaAccess.errorTenantProvisioningFailed',
  identity_provider_unavailable: 'shell.auth.noKarexiaAccess.errorIdentityProviderUnavailable',
  network: 'shell.auth.noKarexiaAccess.errorNetwork',
  unknown: 'shell.auth.noKarexiaAccess.errorUnknown',
};

type LoadState = { kind: 'loading' } | { kind: 'ready'; status: KarexiaAccessStatus } | { kind: 'error' };

export function NoKarexiaAccess() {
  const { t, locale } = useT();
  const auth = useAuth();
  const email = typeof auth.user?.profile?.email === 'string' ? auth.user.profile.email : '';

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [orgName, setOrgName] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<WorkspaceErrorCode | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    let alive = true;
    getKarexiaAccessStatus()
      .then((status) => alive && setLoad({ kind: 'ready', status }))
      .catch(() => alive && setLoad({ kind: 'error' }));
    return () => {
      alive = false;
    };
  }, []);

  const signOut = () => {
    resetNoKarexiaAccess();
    void auth.signoutRedirect({ post_logout_redirect_uri: window.location.origin });
  };

  const switchAccount = async () => {
    resetNoKarexiaAccess();
    // Drop the local session first so the new sign-in is not short-circuited
    // by the cached user, then force Keycloak's credential prompt.
    await auth.removeUser();
    await auth.signinRedirect({ prompt: 'login', state: { returnTo: '/' } });
  };

  const status = load.kind === 'ready' ? load.status : null;
  const invites = status?.pending_invitations ?? [];
  const waitingInvites = status?.pending_invitation_count ?? 0;
  const canCreate = !!status?.self_serve_available;
  const valid = isValidOrgName(orgName) && acceptTerms;
  const marketingWording = t('shell.auth.noKarexiaAccess.marketingOptIn');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || inFlight.current) return;
    let skip = false;
    if (waitingInvites > 0) {
      if (!window.confirm(t('shell.auth.noKarexiaAccess.confirmSkipInvitations'))) return;
      skip = true;
    }
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await createWorkspace({
        org_name: normaliseOrgName(orgName),
        accept_terms: acceptTerms,
        accept_privacy: acceptTerms,
        marketing_opt_in: marketing,
        marketing_wording: marketing ? marketingWording : null,
        locale,
        source_url: window.location.href.slice(0, 300),
        skip_pending_invitations: skip,
      });
    } catch (err) {
      setError(workspaceErrorCode(err));
      setSubmitting(false);
      inFlight.current = false;
      return;
    }
    // New OWNER role + tenant claim only appear in a NEW token.
    setRefreshing(true);
    resetNoKarexiaAccess();
    try {
      await auth.signinSilent();
    } catch {
      await auth.signinRedirect({ state: { returnTo: '/' } });
      return;
    }
    window.location.assign('/');
  };

  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 py-10 text-center"
      data-testid="no-karexia-access"
    >
      <h1 className="text-xl font-semibold text-slate-900">{t('shell.auth.noKarexiaAccess.title')}</h1>
      {email ? (
        <p className="text-slate-700">
          {t('shell.auth.noKarexiaAccess.signedInAs', { email })}
        </p>
      ) : null}
      <p className="max-w-md text-slate-600">{t('shell.auth.noKarexiaAccess.explanation')}</p>

      {load.kind === 'loading' ? (
        <p className="text-sm text-slate-500">{t('shell.auth.noKarexiaAccess.loadingStatus')}</p>
      ) : null}
      {load.kind === 'error' ? (
        <>
          <p className="text-sm text-amber-700">{t('shell.auth.noKarexiaAccess.statusUnavailable')}</p>
          <p className="max-w-md text-sm text-slate-500">{t('shell.auth.noKarexiaAccess.invitationHint')}</p>
        </>
      ) : null}

      {invites.length > 0 ? (
        <section className="w-full max-w-md rounded border border-indigo-200 bg-indigo-50 p-4 text-left" data-testid="pending-invitations">
          <h2 className="font-semibold text-slate-900">{t('shell.auth.noKarexiaAccess.invitationsTitle')}</h2>
          <p className="mt-1 text-sm text-slate-700">{t('shell.auth.noKarexiaAccess.invitationsIntro')}</p>
          <ul className="mt-3 flex flex-col gap-2">
            {invites.map((inv) => (
              <li key={inv.accept_path} className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-600">
                  {t('shell.auth.noKarexiaAccess.invitationRole', { role: inv.org_role })}
                </span>
                <a
                  href={inv.accept_path}
                  className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white"
                >
                  {t('shell.auth.noKarexiaAccess.acceptInvitation')}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {status && invites.length === 0 && waitingInvites > 0 ? (
        <p className="max-w-md text-sm text-indigo-800" data-testid="invitation-unverified">
          {t('shell.auth.noKarexiaAccess.invitationUnverifiedEmail')}
        </p>
      ) : null}
      {status && waitingInvites === 0 && !canCreate ? (
        <p className="max-w-md text-sm text-slate-500">{t('shell.auth.noKarexiaAccess.invitationHint')}</p>
      ) : null}

      {canCreate ? (
        <section className="w-full max-w-md rounded border border-slate-200 p-4 text-left" data-testid="create-workspace">
          <h2 className="font-semibold text-slate-900">{t('shell.auth.noKarexiaAccess.createTitle')}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {waitingInvites > 0
              ? t('shell.auth.noKarexiaAccess.createSecondary')
              : t('shell.auth.noKarexiaAccess.createIntro')}
          </p>
          <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              {t('shell.auth.noKarexiaAccess.orgNameLabel')}
              <input
                className="rounded border border-slate-300 px-3 py-2 text-slate-900"
                value={orgName}
                maxLength={ORG_NAME_MAX + 20}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder={t('shell.auth.noKarexiaAccess.orgNamePlaceholder')}
                aria-describedby="kx-org-name-hint"
                disabled={submitting}
              />
              <span id="kx-org-name-hint" className="text-xs text-slate-500">
                {t('shell.auth.noKarexiaAccess.orgNameHint')}
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                disabled={submitting}
                data-testid="accept-terms"
              />
              <span>
                {t('shell.auth.noKarexiaAccess.acceptTermsPrefix')}{' '}
                <a href="/conditions" target="_blank" rel="noopener noreferrer" className="underline">
                  {t('shell.auth.noKarexiaAccess.termsLink')}
                </a>{' '}
                {t('shell.auth.noKarexiaAccess.acceptTermsAnd')}{' '}
                <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="underline">
                  {t('shell.auth.noKarexiaAccess.privacyLink')}
                </a>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                disabled={submitting}
                data-testid="marketing-opt-in"
              />
              <span>{marketingWording}</span>
            </label>
            {error ? (
              <p role="alert" className="text-sm text-red-600" data-testid="workspace-error">
                {t(ERROR_KEYS[error])}
              </p>
            ) : null}
            {refreshing ? (
              <p className="text-sm text-emerald-700">{t('shell.auth.noKarexiaAccess.refreshing')}</p>
            ) : null}
            <button
              type="submit"
              className="rounded bg-slate-900 px-4 py-2 font-semibold text-white disabled:opacity-50"
              disabled={!valid || submitting}
            >
              {submitting ? t('shell.auth.noKarexiaAccess.submitting') : t('shell.auth.noKarexiaAccess.submit')}
            </button>
          </form>
        </section>
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          className="rounded bg-slate-900 px-4 py-2 text-white"
          onClick={signOut}
        >
          {t('shell.auth.noKarexiaAccess.signOut')}
        </button>
        <button
          type="button"
          className="rounded border border-slate-300 px-4 py-2 text-slate-900"
          onClick={() => void switchAccount()}
        >
          {t('shell.auth.noKarexiaAccess.useAnotherAccount')}
        </button>
      </div>
    </main>
  );
}
